import _ from 'lodash';
import { IconType } from 'react-icons';
import {
    TbArrowsSplit,
    TbBuildingBank,
    TbCalendarSearch,
    TbChartBar,
    TbFileText,
    TbFileTypePdf,
    TbHistory,
    TbLayoutDashboard,
    TbMapPin,
    TbNews,
    TbPlane,
    TbShip,
    TbTransactionDollar,
    TbUserSearch,
    TbUserStar,
} from 'react-icons/tb';

import type { SearchTabKey } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchTabs';
import { unwrapSearchFilters } from '@/shared/components/filters/searchFilters';

type SearchFilterTemplateDomain = Exclude<SearchTabKey, 'all'>;
type WorkspaceResourceType =
    | 'dashboards'
    | 'folders'
    | 'investments'
    | 'lists'
    | 'memos'
    | 'pipelines'
    | 'teams';
type PeopleSource = 'Gmail' | 'Microsoft 365 Outlook' | 'enrichment' | 'fmpExecutives';
type PeopleRole =
    | 'accounting'
    | 'education'
    | 'engineering'
    | 'finance'
    | 'health'
    | 'human_resources'
    | 'legal'
    | 'marketing'
    | 'operations'
    | 'real_estate'
    | 'sales';
type DocumentType = 'connected' | 'publicFilings';
type DocumentFormat = 'docx' | 'epub' | 'pdf' | 'sheet' | 'slide' | 'text';
type EconomicCategory = 'macro' | 'market' | 'treasury';
type TradeMode = 'air' | 'ocean' | 'rail' | 'road';
type TradeRegion = 'Africa' | 'Asia Pacific' | 'Europe' | 'Latin America' | 'Middle East' | 'North America';
type NewsFeed = 'Cryptocurrencies' | 'Currencies' | 'Mainstream' | 'Press Releases';
type NewsType = 'fundraising' | 'insiderTransactions' | 'ipos' | 'mAndA' | 'market';
type EventSource = 'dividend' | 'economic';
type EventImpact = 'high' | 'low' | 'medium';

interface SearchFilterTemplateFieldMap {
    companies: {
        exchanges: string[];
        location: { city: string; country: string; stateTerritory: string; zipCode: string };
        sectors: string[];
    };
    documents: {
        formats: DocumentFormat[];
        types: DocumentType[];
    };
    economic: {
        categories: EconomicCategory[];
    };
    events: {
        impacts: EventImpact[];
        sources: EventSource[];
    };
    news: {
        feeds: NewsFeed[];
        types: NewsType[];
    };
    people: {
        jobTitles: string[];
        roles: PeopleRole[];
        sources: PeopleSource[];
    };
    trade: {
        modes: TradeMode[];
        regions: TradeRegion[];
    };
    workspace: {
        resourceTypes: WorkspaceResourceType[];
        updatedWithin: 'day' | 'month' | 'quarter' | 'week';
    };
}

type SearchFilterTemplateChangeForDomain<Domain extends SearchFilterTemplateDomain> = {
    [Field in keyof SearchFilterTemplateFieldMap[Domain]]: {
        domain: Domain;
        field: Field;
        value: SearchFilterTemplateFieldMap[Domain][Field];
    };
}[keyof SearchFilterTemplateFieldMap[Domain]];

export type SearchFilterTemplateChange = {
    [Domain in SearchFilterTemplateDomain]: SearchFilterTemplateChangeForDomain<Domain>;
}[SearchFilterTemplateDomain];

export interface SearchFilterTemplateDefinition {
    Icon: IconType;
    changes: readonly SearchFilterTemplateChange[];
    key: string;
    label: string;
}

const change = <Domain extends SearchFilterTemplateDomain, Field extends keyof SearchFilterTemplateFieldMap[Domain]>(
    domain: Domain,
    field: Field,
    value: SearchFilterTemplateFieldMap[Domain][Field],
): SearchFilterTemplateChange => ({ domain, field, value }) as SearchFilterTemplateChange;

/**
 * Quick-start templates use the same domain, field names, and values as the
 * canonical left-sider controls. Keeping paths relative here prevents a
 * template from becoming an alternate text-search dialect.
 */
export const searchFilterTemplates: Record<SearchTabKey, readonly SearchFilterTemplateDefinition[]> = {
    all: [
        {
            Icon: TbHistory,
            changes: [change('workspace', 'updatedWithin', 'week')],
            key: 'recent-workspace',
            label: 'Recently Updated',
        },
        {
            Icon: TbUserSearch,
            changes: [change('people', 'sources', ['enrichment', 'fmpExecutives'])],
            key: 'enriched-people',
            label: 'Enriched People',
        },
        {
            Icon: TbCalendarSearch,
            changes: [change('events', 'sources', ['economic']), change('events', 'impacts', ['high'])],
            key: 'high-impact-events',
            label: 'High-Impact Events',
        },
    ],
    workspace: [
        {
            Icon: TbChartBar,
            changes: [change('workspace', 'resourceTypes', ['investments'])],
            key: 'workspace-investments',
            label: 'Investments',
        },
        {
            Icon: TbHistory,
            changes: [change('workspace', 'updatedWithin', 'week')],
            key: 'workspace-recent',
            label: 'Updated This Week',
        },
        {
            Icon: TbLayoutDashboard,
            changes: [change('workspace', 'resourceTypes', ['dashboards', 'lists'])],
            key: 'workspace-analysis',
            label: 'Dashboards & Lists',
        },
    ],
    companies: [
        {
            Icon: TbChartBar,
            changes: [change('companies', 'sectors', ['Technology'])],
            key: 'companies-technology',
            label: 'Technology',
        },
        {
            Icon: TbBuildingBank,
            changes: [change('companies', 'exchanges', ['NASDAQ'])],
            key: 'companies-nasdaq',
            label: 'NASDAQ',
        },
        {
            Icon: TbMapPin,
            changes: [
                change('companies', 'location', {
                    city: '',
                    country: 'US',
                    stateTerritory: '',
                    zipCode: '',
                }),
            ],
            key: 'companies-us',
            label: 'U.S. Headquarters',
        },
    ],
    people: [
        {
            Icon: TbUserStar,
            changes: [change('people', 'sources', ['fmpExecutives'])],
            key: 'people-executives',
            label: 'Company Executives',
        },
        {
            Icon: TbUserSearch,
            changes: [
                change('people', 'jobTitles', [
                    'Chief Executive Officer',
                    'Chief Financial Officer',
                    'Chief Operating Officer',
                ]),
            ],
            key: 'people-leadership',
            label: 'Senior Leadership',
        },
        {
            Icon: TbTransactionDollar,
            changes: [change('people', 'roles', ['finance'])],
            key: 'people-finance',
            label: 'Finance Leaders',
        },
    ],
    documents: [
        {
            Icon: TbBuildingBank,
            changes: [change('documents', 'types', ['publicFilings'])],
            key: 'documents-filings',
            label: 'Public Filings',
        },
        {
            Icon: TbFileText,
            changes: [change('documents', 'types', ['connected'])],
            key: 'documents-connected',
            label: 'Connected Documents',
        },
        {
            Icon: TbFileTypePdf,
            changes: [change('documents', 'formats', ['pdf'])],
            key: 'documents-pdf',
            label: 'PDF Documents',
        },
    ],
    economic: [
        {
            Icon: TbTransactionDollar,
            changes: [change('economic', 'categories', ['macro'])],
            key: 'economic-macro',
            label: 'Macroeconomic',
        },
        {
            Icon: TbBuildingBank,
            changes: [change('economic', 'categories', ['treasury'])],
            key: 'economic-treasury',
            label: 'Treasury Rates',
        },
        {
            Icon: TbChartBar,
            changes: [change('economic', 'categories', ['market'])],
            key: 'economic-markets',
            label: 'Markets & Assets',
        },
    ],
    trade: [
        {
            Icon: TbShip,
            changes: [change('trade', 'modes', ['ocean'])],
            key: 'trade-ocean',
            label: 'Ocean Corridors',
        },
        {
            Icon: TbPlane,
            changes: [change('trade', 'modes', ['air'])],
            key: 'trade-air',
            label: 'Air Corridors',
        },
        {
            Icon: TbMapPin,
            changes: [change('trade', 'regions', ['North America'])],
            key: 'trade-north-america',
            label: 'North America',
        },
    ],
    news: [
        {
            Icon: TbNews,
            changes: [change('news', 'feeds', ['Mainstream'])],
            key: 'news-mainstream',
            label: 'Mainstream News',
        },
        {
            Icon: TbArrowsSplit,
            changes: [change('news', 'types', ['mAndA'])],
            key: 'news-ma',
            label: 'M&A Coverage',
        },
        {
            Icon: TbChartBar,
            changes: [change('news', 'types', ['fundraising'])],
            key: 'news-fundraising',
            label: 'Fundraising',
        },
    ],
    events: [
        {
            Icon: TbCalendarSearch,
            changes: [change('events', 'sources', ['economic']), change('events', 'impacts', ['high'])],
            key: 'events-high-impact',
            label: 'High-Impact Economic',
        },
        {
            Icon: TbTransactionDollar,
            changes: [change('events', 'sources', ['dividend'])],
            key: 'events-dividends',
            label: 'Dividend Calendar',
        },
        {
            Icon: TbCalendarSearch,
            changes: [change('events', 'sources', ['economic'])],
            key: 'events-economic',
            label: 'Economic Calendar',
        },
    ],
};

const resolveChangePath = (activeTab: SearchTabKey, templateChange: SearchFilterTemplateChange): string[] =>
    activeTab === 'companies' && templateChange.domain === 'companies'
        ? [String(templateChange.field)]
        : ['searchDomains', templateChange.domain, String(templateChange.field)];

export const applySearchFilterTemplate = (
    filters: unknown,
    activeTab: SearchTabKey,
    template: SearchFilterTemplateDefinition,
): Record<string, unknown> => {
    const nextFilters = _.cloneDeep(unwrapSearchFilters(filters));

    template.changes.forEach((templateChange) => {
        _.set(nextFilters, resolveChangePath(activeTab, templateChange), templateChange.value);
    });

    return nextFilters;
};
